import React, { useState } from 'react';
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { request } from '../../config/helpers/axios-instance';
import { useModalMain } from '../../contexts/modal';
import { getTenantId } from '../../utils/helpers';
import { TENANT_COMPANY_MAP } from '../../utils/constants';

/**
 * Shown when the map/update Evital call reports that one or more order items have
 * an inventoryId that maps to multiple local items. The user picks the correct
 * item per duplicate group, then we re-submit with `selections`.
 */
function EvitalDuplicateResolution({ duplicates, userOrderId, eVitalRXOrderNo, onSuccess }) {
    const tenantId = getTenantId();
    const isHinai = tenantId === TENANT_COMPANY_MAP["EAZY"];
    const { closeModal } = useModalMain();

    // { [inventoryId]: selectedItemId }
    const [selections, setSelections] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const allResolved = duplicates.every((dup) => selections[dup.inventoryId] != null);

    const handleSelect = (inventoryId, itemId) => {
        setSelections((prev) => ({ ...prev, [inventoryId]: itemId }));
    };

    const handleConfirm = async () => {
        if (!allResolved) {
            toast.error("Please select an item for each duplicate medicine");
            return;
        }

        const body = {
            userOrderId,
            selections: duplicates.map((dup) => ({
                inventoryId: dup.inventoryId,
                selectedItemId: selections[dup.inventoryId]
            }))
        };
        if (isHinai) body.hinaiInvoiceNo = eVitalRXOrderNo;
        else body.eVitalRXOrderNo = eVitalRXOrderNo;

        setSubmitting(true);
        const response = await request({
            method: "POST",
            url: isHinai ? `hinai/userorder` : `evitalrx/userorder`,
            data: body
        });
        setSubmitting(false);

        if (response?.data?.success) {
            toast.success("Request Successful");
            if (onSuccess) onSuccess();
            closeModal();
        } else {
            toast.error(response?.data?.error || "Something went wrong");
        }
    };

    return (
        <div className='p-5'>
            <h1 className='text-2xl text-black my-3 font-bold'>Resolve Duplicate Items</h1>
            <p className='text-sm text-gray-600 mb-4'>
                Multiple local items map to the same Evital medicine. Select the correct item for each before mapping.
            </p>

            <div className='space-y-5 max-h-[60vh] overflow-y-auto'>
                {duplicates.map((dup) => (
                    <div key={dup.inventoryId} className='border-2 border-gray-200 rounded-2xl p-4'>
                        <div className='font-semibold mb-2'>
                            {dup.medicineName}
                            <span className='text-xs text-gray-500 ml-2'>(Evital ID: {dup.inventoryId})</span>
                        </div>
                        <div className='space-y-2'>
                            {dup.items.map((item) => (
                                <label
                                    key={item.id}
                                    className='flex items-start gap-2 cursor-pointer p-2 rounded-xl hover:bg-gray-50'
                                >
                                    <input
                                        type="radio"
                                        name={`dup-${dup.inventoryId}`}
                                        checked={selections[dup.inventoryId] === item.id}
                                        onChange={() => handleSelect(dup.inventoryId, item.id)}
                                        className='mt-1'
                                    />
                                    <span>
                                        <span className='font-medium'>{item.name}</span>
                                        <span className='block text-xs text-gray-500'>
                                            ID: {item.id}
                                            {item.manufacture ? ` · ${item.manufacture}` : ''}
                                            {item.size ? ` · ${item.size}` : ''}
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <button
                type='button'
                disabled={!allResolved || submitting}
                onClick={handleConfirm}
                className="w-full mt-5 bg-elm-500 text-white-100 hover:bg-elm-700 font-semibold font-sans py-2 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {submitting ? "Submitting..." : "Confirm & Map"}
            </button>
        </div>
    );
}

export default EvitalDuplicateResolution;

EvitalDuplicateResolution.propTypes = {
    duplicates: PropTypes.arrayOf(
        PropTypes.shape({
            inventoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            medicineName: PropTypes.string,
            items: PropTypes.array
        })
    ).isRequired,
    userOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eVitalRXOrderNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSuccess: PropTypes.func
};
